/**
 * Formatting for the owner dashboard.
 *
 * Two rules, both of which exist because getting them wrong is invisible until
 * it matters to somebody far away:
 *
 *  1. **The currency comes from the business, never from here.** No symbol is
 *     written into this file, and no locale is assumed. A Nigerian shop sees
 *     ₦, an American one sees $, and a business in a currency with no minor
 *     unit sees no decimals — because `Intl` is told the code and works it out.
 *  2. **The business's day is not the browser's day.** A phone in London
 *     looking at a Lagos shop must see Lagos's "today", or the owner reconciles
 *     a day that has not finished.
 */

/** Money, in whatever currency this business keeps its books. */
export function money(value: string | number | null | undefined, currencyCode: string): string {
  const amount = Number(value ?? 0);

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
      // Let the currency decide: JPY has no minor unit, and forcing two
      // decimals onto it produces something no Japanese reader recognises.
      currencyDisplay: 'narrowSymbol',
    }).format(amount);
  } catch {
    // An unknown or malformed code must not blank the screen. The number is
    // the part the owner needs; the code beside it is still unambiguous.
    return `${currencyCode} ${amount.toFixed(2)}`;
  }
}

/** A share, as a percentage. Null when there is nothing to take a share of. */
export function percent(ratio: string | number | null | undefined): string {
  if (ratio === null || ratio === undefined || ratio === '') return '—';
  return `${(Number(ratio) * 100).toFixed(1)}%`;
}

/** A whole number of units, grouped for reading at a glance. */
export function quantity(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat().format(value);
}

/**
 * Today, in the business's timezone.
 *
 * `toISOString()` would give UTC, which is a different date from Lagos for an
 * hour every day and from Los Angeles for seven. An owner closing up at 11pm
 * must not be shown tomorrow's empty page.
 */
export function todayIn(timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '01';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** A date offset by whole days, still in the business's timezone. */
export function shiftDays(isoDate: string, days: number): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  // Built in UTC and read back in UTC, so the arithmetic cannot be dragged
  // across a boundary by the browser's own offset.
  const base = new Date(Date.UTC(year!, month! - 1, day!));
  base.setUTCDate(base.getUTCDate() + days);
  return base.toISOString().slice(0, 10);
}

/** The first day of the month containing `isoDate`. */
export function startOfMonth(isoDate: string): string {
  return `${isoDate.slice(0, 7)}-01`;
}

export interface DateRange {
  from: string;
  to: string;
}

export type PresetKey = 'today' | 'yesterday' | 'last7' | 'last30' | 'month' | 'custom';

export const PRESET_LABELS: Record<PresetKey, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last7: 'Last 7 days',
  last30: 'Last 30 days',
  month: 'This month',
  custom: 'Custom',
};

/**
 * Resolve a preset against the business's own calendar.
 *
 * Every range is computed from `todayIn(timezone)` rather than from the
 * browser's clock, so the same preset means the same thing whether the owner
 * is in the shop or on a plane.
 */
export function resolvePreset(preset: PresetKey, timezone: string): DateRange {
  const today = todayIn(timezone);

  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const day = shiftDays(today, -1);
      return { from: day, to: day };
    }
    case 'last7':
      return { from: shiftDays(today, -6), to: today };
    case 'last30':
      return { from: shiftDays(today, -29), to: today };
    case 'month':
      return { from: startOfMonth(today), to: today };
    case 'custom':
    default:
      return { from: today, to: today };
  }
}

/** A timestamp, shown in the business's timezone rather than the reader's. */
export function moment(iso: string | null | undefined, timezone: string): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso));
}

export type Tone = 'good' | 'warn' | 'bad' | 'neutral';

/**
 * How a cash variance should read.
 *
 * Short is bad, over is worth a look rather than a celebration — an overage
 * usually means a sale went unrecorded, which is the same failure as a
 * shortage wearing a friendlier face. Exact is the only good outcome.
 */
export function varianceTone(variance: string | number | null | undefined): Tone {
  if (variance === null || variance === undefined) return 'neutral';
  const value = Number(variance);
  if (value === 0) return 'good';
  return value < 0 ? 'bad' : 'warn';
}
